import { InputSwitch } from "primereact/inputswitch";
import { Card } from "primereact/card";
import { Tooltip } from "primereact/tooltip";
import { classNames } from "primereact/utils";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SettingsValues {
  requireRetake: boolean;
  includeDescriptionOnCertificate: boolean;
  activatePath: boolean;
  pathArchived?: boolean;
  restrictPath?: boolean;
  pathRestricted?: boolean;
}

interface PathSettingsStepProps {
  values: SettingsValues;
  handleChange: (newValues: SettingsValues) => void;
  isNew: boolean;
  pathArchived?: boolean;
  contentTypeLabel: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PathSettingsStep = ({
  values,
  handleChange,
  isNew,
  pathArchived = false,
  contentTypeLabel,
}: PathSettingsStepProps) => {
  const handleSettingChange = (field: keyof SettingsValues, value: boolean) => {
    handleChange({ ...values, [field]: value });
  };

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      {/* Learner Settings */}
      <Card className={classNames(themeClasses.bgElevation2, "min-h-52")}>
        <div className="mb-2">
          <h4 className="font-semibold text-xl">1. Learner Settings</h4>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="body-sm">
                Require Learners to re-take previously completed lessons
              </label>
              <p className="body-xs text-secondary mt-1">
                When enabled, learners must complete lessons again even if they were previously
                completed
              </p>
            </div>
            <div id="requireRetake-switch-wrapper">
              <InputSwitch
                checked={values.requireRetake || false}
                onChange={e => handleSettingChange("requireRetake", e.value ?? false)}
                disabled={!isNew}
                className="ml-3"
                aria-label="Require learners to re-take previously completed lessons"
              />
              {(!isNew || values.requireRetake) && (
                <Tooltip target="#requireRetake-switch-wrapper" position="top" className="w-96">
                  {!isNew
                    ? "This setting cannot be changed after the path is created."
                    : "Once enabled, this setting cannot be disabled. To count previously completed lessons as complete, you can clone this path."}
                </Tooltip>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="body-sm">
                Include the {contentTypeLabel.toLowerCase()} description on completed certificates
              </label>
              <p className="body-xs text-secondary mt-1">
                When enabled, the {contentTypeLabel.toLowerCase()} description will be included on
                learner certificates
              </p>
            </div>
            <InputSwitch
              checked={values.includeDescriptionOnCertificate || false}
              onChange={e =>
                handleSettingChange("includeDescriptionOnCertificate", e.value ?? false)
              }
              className="ml-3"
              aria-label={`Include ${contentTypeLabel.toLowerCase()} description on certificates`}
            />
          </div>
        </div>
      </Card>

      {/* Status */}
      <Card className={classNames(themeClasses.bgElevation2, "min-h-52")}>
        <div className="mb-2">
          <h4 className="font-semibold text-xl">2. {contentTypeLabel} Status</h4>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="body-sm">Activate {contentTypeLabel}</label>
              <p className="body-xs text-secondary mt-1">
                When enabled, this {contentTypeLabel.toLowerCase()} will be available to learners
              </p>
            </div>
            <InputSwitch
              checked={values.activatePath || false}
              onChange={e => handleSettingChange("activatePath", e.value ?? false)}
              className="ml-3"
              aria-label={`Activate ${contentTypeLabel}`}
            />
          </div>

          {!isNew && (
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label className="body-sm">
                  {pathArchived ? `Unarchive ${contentTypeLabel}` : `Archive ${contentTypeLabel}`}
                </label>
                <p className="body-xs text-secondary mt-1">
                  When enabled, this {contentTypeLabel.toLowerCase()} will be{" "}
                  {pathArchived ? "unarchived" : "archived"}
                </p>
              </div>
              <InputSwitch
                checked={pathArchived || false}
                onChange={e => handleSettingChange("pathArchived", e.value ?? false)}
                className="ml-3"
                aria-label={
                  pathArchived ? `Unarchive ${contentTypeLabel}` : `Archive ${contentTypeLabel}`
                }
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PathSettingsStep;
